export type PowerPointShapeGroup =
  | "lines"
  | "rectangles"
  | "basic"
  | "blockArrows"
  | "equation"
  | "flowchart"
  | "starsAndBanners"
  | "callouts"
  | "actionButtons"
  | "legacy";

export interface PowerPointShapeCatalogGroup {
  readonly group: PowerPointShapeGroup;
  readonly label: string;
  readonly shapeTypes: readonly string[];
}

export interface PowerPointLineTool {
  readonly id: string;
  readonly label: string;
  readonly connectorType?: "Straight" | "Elbow" | "Curve";
  readonly arrowheads: "none" | "end" | "both";
  readonly freeformTool?: "curve" | "freeform" | "scribble";
}

export const powerPointLineTools: readonly PowerPointLineTool[] = [
  { id: "line", label: "Line", connectorType: "Straight", arrowheads: "none" },
  {
    id: "lineArrow",
    label: "Arrow",
    connectorType: "Straight",
    arrowheads: "end",
  },
  {
    id: "lineDoubleArrow",
    label: "Double Arrow",
    connectorType: "Straight",
    arrowheads: "both",
  },
  {
    id: "straightConnector",
    label: "Straight Connector",
    connectorType: "Straight",
    arrowheads: "none",
  },
  {
    id: "straightArrowConnector",
    label: "Straight Arrow Connector",
    connectorType: "Straight",
    arrowheads: "end",
  },
  {
    id: "straightDoubleArrowConnector",
    label: "Straight Double-Arrow Connector",
    connectorType: "Straight",
    arrowheads: "both",
  },
  {
    id: "elbowConnector",
    label: "Elbow Connector",
    connectorType: "Elbow",
    arrowheads: "none",
  },
  {
    id: "elbowArrowConnector",
    label: "Elbow Arrow Connector",
    connectorType: "Elbow",
    arrowheads: "end",
  },
  {
    id: "elbowDoubleArrowConnector",
    label: "Elbow Double-Arrow Connector",
    connectorType: "Elbow",
    arrowheads: "both",
  },
  {
    id: "curvedConnector",
    label: "Curved Connector",
    connectorType: "Curve",
    arrowheads: "none",
  },
  {
    id: "curvedArrowConnector",
    label: "Curved Arrow Connector",
    connectorType: "Curve",
    arrowheads: "end",
  },
  {
    id: "curvedDoubleArrowConnector",
    label: "Curved Double-Arrow Connector",
    connectorType: "Curve",
    arrowheads: "both",
  },
  { id: "curve", label: "Curve", arrowheads: "none", freeformTool: "curve" },
  {
    id: "freeform",
    label: "Freeform: Shape",
    arrowheads: "none",
    freeformTool: "freeform",
  },
  {
    id: "scribble",
    label: "Freeform: Scribble",
    arrowheads: "none",
    freeformTool: "scribble",
  },
];

export const powerPointShapeCatalog: readonly PowerPointShapeCatalogGroup[] = [
  {
    group: "rectangles",
    label: "Rectangles",
    shapeTypes: [
      "Rectangle",
      "RoundRectangle",
      "Round1Rectangle",
      "Round2SameRectangle",
      "Round2DiagonalRectangle",
      "SnipRoundRectangle",
      "Snip1Rectangle",
      "Snip2SameRectangle",
      "Snip2DiagonalRectangle",
      "Bevel",
    ],
  },
  {
    group: "basic",
    label: "Basic Shapes",
    shapeTypes: [
      "LineInverse",
      "Triangle",
      "RightTriangle",
      "Diamond",
      "Parallelogram",
      "Trapezoid",
      "NonIsoscelesTrapezoid",
      "Pentagon",
      "Hexagon",
      "Heptagon",
      "Octagon",
      "Decagon",
      "Dodecagon",
      "Plaque",
      "Ellipse",
      "Teardrop",
      "PieWedge",
      "Pie",
      "BlockArc",
      "Donut",
      "NoSmoking",
      "Cube",
      "Can",
      "LightningBolt",
      "Heart",
      "Sun",
      "Moon",
      "SmileyFace",
      "FoldedCorner",
      "Frame",
      "HalfFrame",
      "Corner",
      "DiagonalStripe",
      "Chord",
      "Arc",
      "LeftBracket",
      "RightBracket",
      "LeftBrace",
      "RightBrace",
      "BracketPair",
      "BracePair",
      "Cloud",
      "Plus",
      "Gear6",
      "Gear9",
      "Funnel",
      "CornerTabs",
      "SquareTabs",
      "PlaqueTabs",
      "ChartX",
      "ChartStar",
      "ChartPlus",
    ],
  },
  {
    group: "blockArrows",
    label: "Block Arrows",
    shapeTypes: [
      "HomePlate",
      "Chevron",
      "RightArrow",
      "LeftArrow",
      "UpArrow",
      "DownArrow",
      "StripedRightArrow",
      "NotchedRightArrow",
      "BentUpArrow",
      "LeftRightArrow",
      "UpDownArrow",
      "LeftUpArrow",
      "LeftRightUpArrow",
      "QuadArrow",
      "BentArrow",
      "UturnArrow",
      "CircularArrow",
      "LeftCircularArrow",
      "LeftRightCircularArrow",
      "CurvedRightArrow",
      "CurvedLeftArrow",
      "CurvedUpArrow",
      "CurvedDownArrow",
      "SwooshArrow",
    ],
  },
  {
    group: "equation",
    label: "Equation Shapes",
    shapeTypes: [
      "MathPlus",
      "MathMinus",
      "MathMultiply",
      "MathDivide",
      "MathEqual",
      "MathNotEqual",
    ],
  },
  {
    group: "flowchart",
    label: "Flowchart",
    shapeTypes: [
      "FlowChartProcess",
      "FlowChartDecision",
      "FlowChartInputOutput",
      "FlowChartPredefinedProcess",
      "FlowChartInternalStorage",
      "FlowChartDocument",
      "FlowChartMultidocument",
      "FlowChartTerminator",
      "FlowChartPreparation",
      "FlowChartManualInput",
      "FlowChartManualOperation",
      "FlowChartConnector",
      "FlowChartPunchedCard",
      "FlowChartPunchedTape",
      "FlowChartSummingJunction",
      "FlowChartOr",
      "FlowChartCollate",
      "FlowChartSort",
      "FlowChartExtract",
      "FlowChartMerge",
      "FlowChartOfflineStorage",
      "FlowChartOnlineStorage",
      "FlowChartMagneticTape",
      "FlowChartMagneticDisk",
      "FlowChartMagneticDrum",
      "FlowChartDisplay",
      "FlowChartDelay",
      "FlowChartAlternateProcess",
      "FlowChartOffpageConnector",
    ],
  },
  {
    group: "starsAndBanners",
    label: "Stars and Banners",
    shapeTypes: [
      "Star4",
      "Star5",
      "Star6",
      "Star7",
      "Star8",
      "Star10",
      "Star12",
      "Star16",
      "Star24",
      "Star32",
      "IrregularSeal1",
      "IrregularSeal2",
      "Ribbon",
      "Ribbon2",
      "EllipseRibbon",
      "EllipseRibbon2",
      "LeftRightRibbon",
      "VerticalScroll",
      "HorizontalScroll",
      "Wave",
      "DoubleWave",
    ],
  },
  {
    group: "callouts",
    label: "Callouts",
    shapeTypes: [
      "LeftArrowCallout",
      "RightArrowCallout",
      "UpArrowCallout",
      "DownArrowCallout",
      "LeftRightArrowCallout",
      "UpDownArrowCallout",
      "QuadArrowCallout",
      "Callout1",
      "Callout2",
      "Callout3",
      "AccentCallout1",
      "AccentCallout2",
      "AccentCallout3",
      "BorderCallout1",
      "BorderCallout2",
      "BorderCallout3",
      "AccentBorderCallout1",
      "AccentBorderCallout2",
      "AccentBorderCallout3",
      "WedgeRectCallout",
      "WedgeRRectCallout",
      "WedgeEllipseCallout",
      "CloudCallout",
    ],
  },
  {
    group: "actionButtons",
    label: "Action Buttons",
    shapeTypes: [
      "ActionButtonBlank",
      "ActionButtonHome",
      "ActionButtonHelp",
      "ActionButtonInformation",
      "ActionButtonForwardNext",
      "ActionButtonBackPrevious",
      "ActionButtonEnd",
      "ActionButtonBeginning",
      "ActionButtonReturn",
      "ActionButtonDocument",
      "ActionButtonSound",
      "ActionButtonMovie",
    ],
  },
  {
    group: "legacy",
    label: "Legacy MsoAutoShapeType only",
    shapeTypes: [
      "Balloon",
      "LineCallout4",
      "LineCallout4NoBorder",
      "LineCallout4AccentBar",
      "LineCallout4BorderAndAccentBar",
    ],
  },
];

export const powerPointModernGeometricShapeTypes = powerPointShapeCatalog
  .filter((group) => group.group !== "legacy")
  .flatMap((group) => group.shapeTypes);

export const powerPointPresetShapeTypes = powerPointShapeCatalog.flatMap(
  (group) => group.shapeTypes,
);

export const powerPointShapeTypeSet = new Set(powerPointPresetShapeTypes);
